
import { useState, useEffect } from "react";

const FooterAdmin = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/footer-section');
        const result = await response.json();
        
        if (result.success && result.data) {
          setFooterData(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch footer data");
        }
      } catch (err) {
        setMessage({ text: `Error: ${err.message}`, type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchFooterData();
  }, []);

  const handleInputChange = (section, field, value) => {
    if (!footerData) return;
    
    setFooterData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSocialLinkChange = (index, field, value) => {
    if (!footerData || !footerData.topSection || !footerData.topSection.socialLinks) return;
    
    setFooterData(prev => {
      const newSocialLinks = [...prev.topSection.socialLinks];
      newSocialLinks[index] = {
        ...newSocialLinks[index],
        [field]: value
      };
      
      return {
        ...prev,
        topSection: {
          ...prev.topSection,
          socialLinks: newSocialLinks
        }
      };
    });
  };

  const handleQuickLinkChange = (index, field, value) => {
    if (!footerData || !footerData.middleSection || !footerData.middleSection.quickLinks) return;
    
    setFooterData(prev => {
      const newQuickLinks = [...prev.middleSection.quickLinks];
      newQuickLinks[index] = {
        ...newQuickLinks[index],
        [field]: value
      };
      
      return {
        ...prev,
        middleSection: {
          ...prev.middleSection,
          quickLinks: newQuickLinks
        }
      };
    });
  };

  const handleLegalLinkChange = (index, field, value) => {
    if (!footerData || !footerData.bottomSection || !footerData.bottomSection.legalLinks) return;
    
    setFooterData(prev => {
      const newLegalLinks = [...prev.bottomSection.legalLinks];
      newLegalLinks[index] = {
        ...newLegalLinks[index],
        [field]: value
      };
      
      return {
        ...prev,
        bottomSection: {
          ...prev.bottomSection,
          legalLinks: newLegalLinks
        }
      };
    });
  };

  const handleContactInfoChange = (field, value) => {
    if (!footerData || !footerData.middleSection || !footerData.middleSection.contactInfo) return;
    
    setFooterData(prev => ({
      ...prev,
      middleSection: {
        ...prev.middleSection,
        contactInfo: {
          ...prev.middleSection.contactInfo,
          [field]: value
        }
      }
    }));
  };

  const addSocialLink = () => {
    if (!footerData || !footerData.topSection) return;
    
    setFooterData(prev => ({
      ...prev,
      topSection: {
        ...prev.topSection,
        socialLinks: [
          ...(prev.topSection.socialLinks || []),
          { platform: "", url: "", isActive: true }
        ]
      }
    }));
  };

  const deleteSocialLink = (index) => {
    if (!footerData || !footerData.topSection || !footerData.topSection.socialLinks) return;
    
    setFooterData(prev => {
      const newSocialLinks = [...prev.topSection.socialLinks];
      newSocialLinks.splice(index, 1);
      
      return {
        ...prev,
        topSection: {
          ...prev.topSection,
          socialLinks: newSocialLinks
        }
      };
    });
  };

  const addQuickLink = () => {
    if (!footerData || !footerData.middleSection) return;
    
    setFooterData(prev => ({
      ...prev,
      middleSection: {
        ...prev.middleSection,
        quickLinks: [
          ...(prev.middleSection.quickLinks || []),
          { text: "", url: "", isActive: true }
        ]
      }
    }));
  };

  const deleteQuickLink = (index) => {
    if (!footerData || !footerData.middleSection || !footerData.middleSection.quickLinks) return;
    
    setFooterData(prev => {
      const newQuickLinks = [...prev.middleSection.quickLinks];
      newQuickLinks.splice(index, 1);
      
      return {
        ...prev,
        middleSection: {
          ...prev.middleSection,
          quickLinks: newQuickLinks
        }
      };
    });
  };

  const addLegalLink = () => {
    if (!footerData || !footerData.bottomSection) return;
    
    setFooterData(prev => ({
      ...prev,
      bottomSection: {
        ...prev.bottomSection,
        legalLinks: [
          ...(prev.bottomSection.legalLinks || []),
          { text: "", url: "", isActive: true }
        ]
      }
    }));
  };

  const deleteLegalLink = (index) => {
    if (!footerData || !footerData.bottomSection || !footerData.bottomSection.legalLinks) return;
    
    setFooterData(prev => {
      const newLegalLinks = [...prev.bottomSection.legalLinks];
      newLegalLinks.splice(index, 1);
      
      return {
        ...prev,
        bottomSection: {
          ...prev.bottomSection,
          legalLinks: newLegalLinks
        }
      };
    });
  };

  const handleSave = async () => {
    if (!footerData) return;
    
    setSaving(true);
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/footer-section', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(footerData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({ text: "Footer updated successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to update footer");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/footer-section/reset', {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setFooterData(result.data);
        setMessage({ text: "Footer reset to default successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to reset footer");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading footer data...</span>
        </div>
        <span className="ms-2">Loading footer data...</span>
      </div>
    );
  }

  if (!footerData) {
    return (
      <div className="alert alert-danger text-center py-5" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No footer data available
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header  ">
              <h2 className=" text-black mb-0">
                Footer Management
              </h2>
            </div>
            <div className="card-body">
              {message.text && (
                <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show`} role="alert">
                  <i className={`fas ${message.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"} me-2`}></i>
                  {message.text}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setMessage({ text: "", type: "" })}
                    aria-label="Close"
                  ></button>
                </div>
              )}
              
              {/* Footer Active Toggle */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="footerActive"
                      checked={footerData.isActive || false}
                      onChange={(e) => setFooterData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    <label className="form-check-label fw-bold" htmlFor="footerActive">
                      <i className="fas fa-toggle-on me-2"></i>
                      Footer Active
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="row g-4 mb-4">
               
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-header bg-info text-white">
                      <h3 className="card-title h5 mb-0">
                        <i className="fas fa-arrow-up me-2"></i>
                        Top Section
                      </h3>
                    </div>
                    <div className="card-body">
                      {/* <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-video me-1"></i>
                          Video URL
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={footerData.topSection?.videoUrl || ""}
                          onChange={(e) => handleInputChange("topSection", "videoUrl", e.target.value)}
                          placeholder="Enter video URL"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-image me-1"></i>
                          Image URL
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={footerData.topSection?.imageUrl || ""}
                          onChange={(e) => handleInputChange("topSection", "imageUrl", e.target.value)}
                          placeholder="Enter image URL"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-info-circle me-1"></i>
                          Tooltip Text
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={footerData.topSection?.tooltipText || ""}
                          onChange={(e) => handleInputChange("topSection", "tooltipText", e.target.value)}
                          placeholder="Enter tooltip text"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-font me-1"></i>
                          Logo Text
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={footerData.topSection?.logoText || ""}
                          onChange={(e) => handleInputChange("topSection", "logoText", e.target.value)}
                          placeholder="Enter logo text"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-align-left me-1"></i>
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={footerData.topSection?.description || ""}
                          onChange={(e) => handleInputChange("topSection", "description", e.target.value)}
                          placeholder="Enter description"
                        />
                      </div> */}
                      
                    
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-semibold mb-0">
                            <i className="fas fa-share-alt me-1"></i>
                            Social Links
                          </label>
                          <button
                            type="button"
                            onClick={addSocialLink}
                            className="btn btn-success btn-sm"
                          >
                            <i className="fas fa-plus me-1"></i>
                            Add Link
                          </button>
                        </div>
                        
                        {footerData.topSection?.socialLinks?.map((link, index) => (
                          <div key={index} className="card mb-2">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-secondary">Link #{index + 1}</span>
                                <div className="d-flex align-items-center">
                                  <div className="form-check form-switch me-2">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`socialActive${index}`}
                                      checked={link.isActive || false}
                                      onChange={(e) => handleSocialLinkChange(index, "isActive", e.target.checked)}
                                    />
                                    <label className="form-check-label small" htmlFor={`socialActive${index}`}>
                                      Active
                                    </label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => deleteSocialLink(index)}
                                    className="btn btn-danger btn-sm"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                              
                              <div className="row g-2">
                                <div className="col-6">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Platform (e.g., facebook)"
                                    value={link.platform || ""}
                                    onChange={(e) => handleSocialLinkChange(index, "platform", e.target.value)}
                                  />
                                </div>
                                <div className="col-6">
                                  <input
                                    type="url"
                                    className="form-control form-control-sm"
                                    placeholder="URL"
                                    value={link.url || ""}
                                    onChange={(e) => handleSocialLinkChange(index, "url", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Middle Section */}
                <div className="col-lg-6">
                  <div className="card h-100">
                    <div className="card-header bg-warning text-dark">
                      <h3 className="card-title h5 mb-0">
                        <i className="fas fa-grip-horizontal me-2"></i>
                        Footer Section
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-image me-1"></i>
                          Logo URL
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={footerData.middleSection?.logoUrl || ""}
                          onChange={(e) => handleInputChange("middleSection", "logoUrl", e.target.value)}
                          placeholder="Enter logo URL"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-align-left me-1"></i>
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={footerData.middleSection?.description || ""}
                          onChange={(e) => handleInputChange("middleSection", "description", e.target.value)}
                          placeholder="Enter description"
                        />
                      </div>
                      
                      {/* Quick Links */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-semibold mb-0">
                            <i className="fas fa-link me-1"></i>
                            Quick Links
                          </label>
                          <button
                            type="button"
                            onClick={addQuickLink}
                            className="btn btn-success btn-sm"
                          >
                            <i className="fas fa-plus me-1"></i>
                            Add Link
                          </button>
                        </div>
                        
                        {footerData.middleSection?.quickLinks?.map((link, index) => (
                          <div key={index} className="card mb-2">
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="badge bg-secondary">Link #{index + 1}</span>
                                <div className="d-flex align-items-center">
                                  <div className="form-check form-switch me-2">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`quickActive${index}`}
                                      checked={link.isActive || false}
                                      onChange={(e) => handleQuickLinkChange(index, "isActive", e.target.checked)}
                                    />
                                    <label className="form-check-label small" htmlFor={`quickActive${index}`}>
                                      Active
                                    </label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => deleteQuickLink(index)}
                                    className="btn btn-danger btn-sm"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </div>
                              
                              <div className="row g-2">
                                <div className="col-6">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Link Text"
                                    value={link.text || ""}
                                    onChange={(e) => handleQuickLinkChange(index, "text", e.target.value)}
                                  />
                                </div>
                                <div className="col-6">
                                  <input
                                    type="url"
                                    className="form-control form-control-sm"
                                    placeholder="URL"
                                    value={link.url || ""}
                                    onChange={(e) => handleQuickLinkChange(index, "url", e.target.value)}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Contact Info */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <i className="fas fa-address-card me-1"></i>
                          Contact Information
                        </label>
                        <div className="mb-2">
                          <div className="input-group">
                            <span className="input-group-text">
                              <i className="fas fa-map-marker-alt"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Address"
                              value={footerData.middleSection?.contactInfo?.address || ""}
                              onChange={(e) => handleContactInfoChange("address", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="mb-2">
                          <div className="input-group">
                            <span className="input-group-text">
                              <i className="fas fa-envelope"></i>
                            </span>
                            <input
                              type="email"
                              className="form-control"
                              placeholder="Email"
                              value={footerData.middleSection?.contactInfo?.email || ""}
                              onChange={(e) => handleContactInfoChange("email", e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="input-group">
                            <span className="input-group-text">
                              <i className="fas fa-phone"></i>
                            </span>
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Phone"
                              value={footerData.middleSection?.contactInfo?.phone || ""}
                              onChange={(e) => handleContactInfoChange("phone", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom Section */}
              <div className="card mb-4">
                <div className="card-header bg-secondary text-white">
                  <h3 className="card-title h5 mb-0">
                    <i className="fas fa-arrow-down me-2"></i>
                    Bottom Section
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <label className="form-label fw-semibold">
                        <i className="fas fa-copyright me-1"></i>
                        Copyright Text
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={footerData.bottomSection?.copyrightText || ""}
                        onChange={(e) => handleInputChange("bottomSection", "copyrightText", e.target.value)}
                        placeholder="Enter copyright text"
                      />
                    </div>
                    
                    {/* <div className="col-lg-6">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">
                          <i className="fas fa-gavel me-1"></i>
                          Legal Links
                        </label>
                        <button
                          type="button"
                          onClick={addLegalLink}
                          className="btn btn-success btn-sm"
                        >
                          <i className="fas fa-plus me-1"></i>
                          Add Link
                        </button>
                      </div>
                      
                      {footerData.bottomSection?.legalLinks?.map((link, index) => (
                        <div key={index} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="badge bg-secondary">Link #{index + 1}</span>
                              <div className="d-flex align-items-center">
                                <div className="form-check form-switch me-2">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`legalActive${index}`}
                                    checked={link.isActive || false}
                                    onChange={(e) => handleLegalLinkChange(index, "isActive", e.target.checked)}
                                  />
                                  <label className="form-check-label small" htmlFor={`legalActive${index}`}>
                                    Active
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => deleteLegalLink(index)}
                                  className="btn btn-danger btn-sm"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                            
                            <div className="row g-2">
                              <div className="col-6">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Link Text"
                                  value={link.text || ""}
                                  onChange={(e) => handleLegalLinkChange(index, "text", e.target.value)}
                                />
                              </div>
                              <div className="col-6">
                                <input
                                  type="url"
                                  className="form-control form-control-sm"
                                  placeholder="URL"
                                  value={link.url || ""}
                                  onChange={(e) => handleLegalLinkChange(index, "url", e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div> */}
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="btn btn-outline-secondary"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-undo me-2"></i>
                      Reset to Default
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterAdmin;